import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Pagination,
  Chip,
} from "@mui/material";
import { EditorPhrase } from "../../api/phrasebook_editor";
import FormattedText from "../common/formatted-text";

interface PhraseUsageDialogProps {
  open: boolean;
  phrase: EditorPhrase | null;
  onClose: () => void;
  usageJobs: Array<{
    job_id: string;
    master_id: string;
    created_at: string;
    status: string;
    phrase_type: string;
    phrase_order: number;
  }>;
  loading: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
  jobsPerPage: number;
}

const PhraseUsageDialog: React.FC<PhraseUsageDialogProps> = ({
  open,
  phrase,
  onClose,
  usageJobs,
  loading,
  currentPage,
  onPageChange,
  jobsPerPage,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Phrase Usage Details
        {phrase && (
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
            "<FormattedText text={phrase.phrase} />" - Used in {phrase.usage_count || 0} jobs
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : usageJobs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No jobs found using this phrase.
          </Typography>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Jobs Using This Phrase
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Master ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Phrase Type</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usageJobs
                    .slice(
                      (currentPage - 1) * jobsPerPage,
                      currentPage * jobsPerPage
                    )
                    .map((job) => (
                      <TableRow key={job.job_id}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.8rem",
                              color: "primary.main",
                              cursor: "pointer",
                              textDecoration: "underline",
                              "&:hover": {
                                color: "primary.dark",
                              },
                            }}
                            onClick={() =>
                              window.open(`/job/${job.job_id}`, "_blank")
                            }
                          >
                            {job.master_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={job.status}
                            size="small"
                            color={
                              job.status === "COMPLETED" ? "success" : "default"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={job.phrase_type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(job.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {usageJobs.length > jobsPerPage && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={Math.ceil(usageJobs.length / jobsPerPage)}
                  page={currentPage}
                  onChange={(_, page) => onPageChange(page)}
                  size="small"
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhraseUsageDialog;
